// src/features/business-functions/adapters/accountDetailAdapter.js

import { ACCOUNT_STATUS } from '../constants/accountConstants';
/**
 * Unwrap Machadalo API envelope and nested account payload.
 * @param {object} raw
 * @returns {object|null}
 */
function unwrapAccountPayload(raw) {
  if (!raw) return null;
  const envelope = raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data)
    ? raw.data
    : raw;
  return envelope?.account ?? envelope;
}

/**
 * Transforms a raw backend account DTO into a normalized frontend model.
 *
 * @param {object} raw — Raw API response object
 * @returns {object} — Normalized account detail
 */
export function adaptAccountDetail(raw) {
  const payload = unwrapAccountPayload(raw);
  if (!payload) return null;

  return {
    id: payload.account_id ?? payload.id ?? '',
    name: payload.name ?? payload.account_name ?? 'Unnamed Account',
    description: payload.description ?? payload.comments ?? '',
    status: payload.is_active === false ? ACCOUNT_STATUS.INACTIVE : ACCOUNT_STATUS.ACTIVE,
    is_active: payload.is_active !== false,
    organisationId: payload.organisation_id ?? payload.organisation ?? '',
    organisationName: payload.organisation_name ?? '',
    billingEmail: payload.email ?? payload.billing_email ?? '',
    contactPhone: payload.phone ?? payload.contact_phone ?? '',
    address: payload.address ?? '',
    createdAt: payload.created_at ?? null,
    updatedAt: payload.updated_at ?? null,
  };
}

/**
 * Transforms raw backend stats DTO into a normalized frontend model.
 * Gracefully defaults all fields so UI never crashes on partial data.
 *
 * @param {object} raw — Raw API response object
 * @returns {object} — Normalized stats model
 */
export function adaptAccountStats(raw) {
  const payload = raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data)
    ? raw.data
    : raw;

  if (!payload) {
    return {
      totalWorkspaces: 0,
      activeUsers: 0,
      totalTables: 0,
      totalAutomations: 0,
      trend: {},
      chartData: [],
      distribution: [],
      recentActivity: [],
    };
  }

  return {
    totalWorkspaces: payload.total_workspaces ?? 0,
    activeUsers: payload.active_users ?? 0,
    totalTables: payload.total_tables ?? 0,
    totalAutomations: payload.total_automations ?? 0,
    trend: {
      workspaces: parseTrend(payload.trend?.workspaces),
      users: parseTrend(payload.trend?.users),
      tables: parseTrend(payload.trend?.tables),
      automations: parseTrend(payload.trend?.automations),
    },
    chartData: Array.isArray(payload.chart_data) ? payload.chart_data : [],
    distribution: Array.isArray(payload.distribution) ? payload.distribution : [],
    recentActivity: Array.isArray(payload.recent_activity) ? payload.recent_activity : [],
  };
}

/**
 * Parses a trend object from backend into a consistent shape.
 * @param {object|null} raw
 * @returns {{ direction: 'up'|'down'|'flat', percentage: number } | null}
 */
function parseTrend(raw) {
  if (!raw || raw.percentage == null) return null;

  const pct = Number(raw.percentage) || 0;
  let direction = 'flat';
  if (pct > 0) direction = 'up';
  else if (pct < 0) direction = 'down';

  return { direction, percentage: Math.abs(pct) };
}
